import { CLIENT_ID, OAUTH_URL, API_URL, LOCALSTORAGE } from 'config';
import type { ErrorResp } from 'models';

/**
 * The standard OAuth2 Credentials Payload
 */
export interface OAuth2CredsRaw {
    access_token: string;
    expires_in: number;
    token_type: string;
    scope: string;
    refresh_token?: string;
}

export interface OAuth2Creds {
    /**
     * Access token required to access secured services
     */
    accessToken: string;
    /**
     * The time in seconds that the access token will last
     */
    expiresIn: number;
    /**
     * The Type of token recieved
     */
    tokenType: string;
    /**
     * The scopes the token has access to
     */
    scopes?: string[];
    /**
     * The refresh token required to get new access tokens
     */
    refreshToken?: string;
    /**
     * The time when the refresh token will expire and must be refreshed
     */
    refreshRefreshTokenTime?: Date;
    /**
     * The time the access token should be refreshed (may be a little before it actually times out)
     */
    refreshTime?: Date;
}

export interface State {
    path: string;
}

/**
 * A multiplier for when the refresh times should actually attempt to refresh.
 * If above one, expired tokens will continue to be used.
 * If too far below one, tokens will refresh more often than it needs to be.
 * If exactly 1, a token may have a brief window of expirey before it is actually refreshed.
 */
const BUFFER_TIME_BEFORE_EXPIREY = 0.95;

const TOKEN_ROUTE = `${OAUTH_URL}/token/`;
const AUTHORISATION_ROUTE = `${OAUTH_URL}/authorize/`;
const LOGOUT_ROUTE = `${API_URL}/users/logout/`;

const rawOAuthCredsToInternal = (
    raw: OAuth2CredsRaw,
    oldTokens?: OAuth2Creds | null,
): OAuth2Creds => ({
    accessToken: raw.access_token,
    expiresIn: raw.expires_in,
    scopes: raw.scope.split(' '),
    tokenType: raw.token_type,
    refreshToken: raw.refresh_token ?? oldTokens?.refreshToken,
    refreshTime: raw.access_token
        ? new Date(
              Date.now() + raw.expires_in * 1000 * BUFFER_TIME_BEFORE_EXPIREY,
          )
        : undefined,
    refreshRefreshTokenTime: raw.refresh_token
        ? new Date(Date.now() + 86_400_000 * BUFFER_TIME_BEFORE_EXPIREY)
        : oldTokens?.refreshRefreshTokenTime,
});

/**
 * Will redirect to the login page with the relevant parameters
 */
export const redirectToAuth = async () => {
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: window.location.origin,
        state: JSON.stringify({
            path: `${window.location.pathname}${window.location.search}`,
        }),
        locale: 'en-au',
    });
    window.location.href = `${AUTHORISATION_ROUTE}?${params.toString()}`;
};

/**
 * Gets a new token along with a refresh token if authCode is provided.
 */
export const getToken = async ({
    authCode,
    tokens,
}: {
    authCode?: string;
    tokens?: OAuth2Creds | null;
}): Promise<OAuth2Creds | ErrorResp> => {
    const params = authCode
        ? new URLSearchParams({
              client_id: CLIENT_ID,
              grant_type: 'authorization_code',
              redirect_uri: window.location.origin,
              code: authCode,
          })
        : new URLSearchParams({
              client_id: CLIENT_ID,
              grant_type: 'refresh_token',
              refresh_token: tokens?.refreshToken ?? '',
          });

    const resp = await fetch(TOKEN_ROUTE, {
        method: 'POST',
        body: params,
    });

    if (!resp.ok) {
        return {
            error: resp.statusText,
            code: resp.status,
        };
    }

    const rawCreds = (await resp.json()) as OAuth2CredsRaw;

    return rawOAuthCredsToInternal(rawCreds, tokens);
};

export const logout = async (): Promise<void> => {
    localStorage.removeItem(LOCALSTORAGE.AUTH);

    const params = new URLSearchParams({
        url: window.location.origin,
    });
    window.location.href = `${LOGOUT_ROUTE}?${params.toString()}`;
};
